import { Button } from '@e6/ds/core';
import { AlertCircle, ArrowLeft, RefreshCw, ShieldX } from 'lucide-react';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface BaseStateProps {
  backHref?: string;
  backLabel?: string;
  className?: string;
}

interface LoadingStateProps extends BaseStateProps {
  message?: string;
}

interface ErrorStateProps extends BaseStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  showIcon?: boolean;
}

interface NotFoundStateProps extends BaseStateProps {
  title?: string;
  message?: string;
}

interface EmptyStateProps extends BaseStateProps {
  title?: string;
  message?: string;
  action?: ReactNode;
}

/**
 * Back button component used across state displays
 */
function BackButton({ href, label = 'Back' }: { href: string; label?: string }) {
  const navigate = useNavigate();
  return (
    <Button variant="ghost" onClick={() => navigate(href)} className="mb-4">
      <ArrowLeft className="mr-2 h-4 w-4" />
      {label}
    </Button>
  );
}

/**
 * Loading state display
 */
export function LoadingState({
  message = 'Loading...',
  backHref,
  backLabel,
  className,
}: LoadingStateProps) {
  return (
    <div className={cn('p-6', className)}>
      {backHref && <BackButton href={backHref} label={backLabel} />}
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{message}</div>
      </div>
    </div>
  );
}

/**
 * Error state display with optional retry functionality
 */
export function ErrorState({
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please try again.',
  backHref,
  backLabel,
  className,
  onRetry,
  retryLabel = 'Try Again',
  showIcon = true,
}: ErrorStateProps) {
  return (
    <div className={cn('flex flex-1 flex-col items-center justify-center pb-24', className)}>
      {backHref && <BackButton href={backHref} label={backLabel} />}
      {showIcon && (
        <div className="bg-destructive/10 mb-4 rounded-full p-4">
          <AlertCircle className="text-destructive h-8 w-8" />
        </div>
      )}
      <h2 className="text-foreground mb-2 text-lg font-semibold">{title}</h2>
      <p className="text-muted-foreground mb-4 max-w-sm text-center text-sm">{message}</p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * Not found state display
 */
export function NotFoundState({
  title = 'Not Found',
  message = 'The resource you are looking for does not exist.',
  backHref,
  backLabel,
  className,
}: NotFoundStateProps) {
  return (
    <div className={cn('p-6', className)}>
      {backHref && <BackButton href={backHref} label={backLabel} />}
      <div className="flex flex-col items-center justify-center py-12">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-muted-foreground text-sm">{message}</p>
      </div>
    </div>
  );
}

/**
 * Invalid URL state display (missing required parameters)
 */
export function InvalidUrlState({
  title = 'Invalid URL',
  message = 'Missing required parameters in the URL.',
  backHref,
  backLabel,
  className,
}: ErrorStateProps) {
  return (
    <div className={cn('p-6', className)}>
      {backHref && <BackButton href={backHref} label={backLabel} />}
      <div className="flex flex-col items-center justify-center py-12">
        <h2 className="text-destructive text-lg font-semibold">{title}</h2>
        <p className="text-muted-foreground text-sm">{message}</p>
      </div>
    </div>
  );
}

interface AccessDeniedStateProps extends BaseStateProps {
  title?: string;
  message?: string;
}

/**
 * Access denied state display (403 Forbidden)
 */
export function AccessDeniedState({
  title = 'Access Denied',
  message = "You don't have permission to access this page. Contact your administrator.",
  backHref,
  backLabel,
  className,
}: AccessDeniedStateProps) {
  return (
    <div className={cn('flex flex-1 flex-col items-center justify-center pb-24', className)}>
      {backHref && <BackButton href={backHref} label={backLabel} />}
      <div className="mb-4 rounded-full bg-amber-100 p-4 dark:bg-amber-900/30">
        <ShieldX className="h-8 w-8 text-amber-600 dark:text-amber-400" />
      </div>
      <h2 className="text-foreground mb-2 text-lg font-semibold">{title}</h2>
      <p className="text-muted-foreground mb-4 max-w-sm text-center text-sm">{message}</p>
    </div>
  );
}

/**
 * Empty state display with optional action
 */
export function EmptyState({
  title = 'No data',
  message = 'No items found.',
  action,
  backHref,
  backLabel,
  className,
}: EmptyStateProps) {
  return (
    <div className={cn('p-6', className)}>
      {backHref && <BackButton href={backHref} label={backLabel} />}
      <div className="flex flex-col items-center justify-center py-12">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-muted-foreground text-sm">{message}</p>
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}
